/**
 * Shared UI theme colors used across all frontend pages.
 * Consolidates ~50% duplicated color definitions.
 */

/**
 * Base theme colors (page backgrounds, gradients, text)
 */
export const THEME_COLORS = {
  // Backgrounds
  pageBg: 'linear-gradient(180deg, #010802 0%, #020A05 47.6%, #121F14 100%)',
  topbarBg: '#010802',
  bottomBarBg: 'linear-gradient(180deg, rgba(1,8,2,0) 0%, #010802 28%, #010802 100%)',

  // Title & hero gradients
  titleGradient: 'linear-gradient(180deg, #F5EC63 0%, #4ACA61 99.27%, #F7DD74 208.57%)',
  statGoldGradient: 'linear-gradient(180deg, #F5EC63 0%, #FD9E1B 100%)',
  statGreenGradient: 'linear-gradient(180deg, #5EE43F 0%, #347E23 100%)',
  levelFillGradient: 'linear-gradient(90deg, #DFD352 0%, #7DB156 100%)',
  walletLogoText: 'radial-gradient(50% 50% at 50% 50%, #ADD741 30.29%, #FDDD38 100%)',
  buttonBg: 'linear-gradient(91deg, #344b21cc -1.21%, #296d26cc 60.35%, #c0c655cc 97.28%)',
  buttonShadow: '0 2px 21px 0 #a4b3fc26',
  actionDivider: 'linear-gradient(90deg, rgba(153, 153, 153, 0.00) 0%, #ADD741 52.4%, rgba(153, 153, 153, 0.00) 100%)',
  statDivider: 'linear-gradient(180deg, transparent 0%, #80681C 50%, transparent 100%)',
  heroGlow: 'radial-gradient(circle at 50% 20%, rgba(245,236,99,0.22) 0%, rgba(74,202,97,0.12) 32%, transparent 64%)',
  orderBookDivider: 'linear-gradient(180deg, #2F5B29 0%, #F5EC63 50%, #2F5B29 100%)',
  statDividerAlt: 'linear-gradient(180deg, transparent 0%, #ADD741 52.4%, transparent 100%)',

  // Text colors
  text: '#E5E7EB',
  muted: '#9CA3AF',
  headerText: '#E5E7EB',

  // Borders
  border: '#FFCF2F',
  softBorder: 'rgba(245, 236, 99, 0.25)',
  buttonBorder: '#d7ff7033',

  // Glass morphism
  glassBg: 'rgba(0, 0, 0, 0.60)',
  glassShadow: 'inset 0 3px 12.1px 5px rgba(245, 236, 99, 0.30)',

  // Component backgrounds
  inputBg: 'rgba(0, 0, 0, 0.35)',
  chipBg: 'rgba(245, 236, 99, 0.08)',
  tabActiveBg: 'rgba(173, 215, 65, 0.18)',
  innerPanelBg: '#0E0B01',
  innerBg: 'rgba(14, 11, 1, 0.78)',
  activeBg: 'rgba(245, 236, 99, 0.08)',
  liveBg: 'rgba(74, 202, 97, 0.16)',

  // Dividers & lines
  divider: 'rgba(128, 104, 28, 0.35)',
  softDivider: 'rgba(128, 104, 28, 0.20)',
  line: 'rgba(245, 236, 99, 0.15)',
  tabBorder: 'rgba(107,114,128,0.3)',
  rangeBorder: 'rgba(245,236,99,0.25)',
  rangeActiveBg: 'rgba(245,236,99,0.1)',

  // Specific components
  dropdownBg: 'rgba(7, 16, 10, 0.95)',
  borderDrawer: 'rgba(255, 207, 47, 0.4)',
  bgLanguage: 'linear-gradient(90deg, rgba(11, 20, 55, 0.02) 0%, rgba(124, 58, 237, 0.02) 100%)',

  // Accent colors
  gold: '#F5EC63',
  yellow: '#FCD749',
  lightYellow: '#FCFDFB',
  activeGreen: '#ADD741',
  chartColor: '#A7F950',
} as const;

/**
 * Semantic status colors
 */
export const STATUS_COLORS = {
  // Positive/Success
  success: '#4ACA61',
  successAlt: '#56D364',
  green: '#ADD741',

  // Negative/Error
  error: '#F74E5E',
  red: '#F74E5E',

  // Information
  info: '#58A6FF',
  blue: '#79C0FF',

  // Warning/Attention
  warning: '#F5EC63',
  amber: '#F5EC63',

  // Neutral/Inactive
  muted: '#9CA3AF',
  inactive: '#6B7280',
} as const;

/**
 * Trading & market state colors
 */
export const MARKET_COLORS = {
  buy: '#4ACA61',
  sell: '#F74E5E',
  positive: '#4ACA61',
  negative: '#F74E5E',
  open: '#79C0FF',
  partial: '#F5EC63',
  done: '#4ACA61',
  cancelled: '#6B7280',
  dispute: '#F74E5E',
  arrow: '#ADD741',
} as const;

/**
 * Tone-based colors for components
 */
export const TONE_COLORS = {
  blue: '#79C0FF',
  green: '#56D364',
  gold: '#F5EC63',
  muted: '#8B949E',
  red: '#F74E5E',
} as const;

/**
 * Profile/Me section colors
 */
export const PROFILE_COLORS = {
  blue: '#79c0ff',
  pink: '#f778ba',
  gold: '#c9a227',
  green: '#56d364',
  yellow: '#e6c060',
  purple: '#bc8cff',
  amber: '#d4a030',
  red: '#f85149',
} as const;

/**
 * Avatar colors
 */
export const AVATAR_COLORS = {
  fill: '#0B1903',
  letter: '#F6CD51',
  ringGradientStart: '#FFFFFF',
  ringGradientEnd: '#F5EC63',
  danger: '#EF4444',
} as const;

/**
 * Shadows & effects
 */
export const SHADOWS = {
  titleShadow: '0 0 12px #312F06',
  projectTitleShadow: '0 0 19.3px rgba(246,205,81,0.5)',
  subtitleShadow: '0 0 16.6px #000000',
  playerIdShadow: '0 4px 4px #000000',
  levelTrackShadow: '0 1px 4px 0 #000000',
  levelFillShadow: '0 0 9.8px 0 rgba(245,236,99,0.4)',
  inviteCodeShadow: '0 0 21px rgba(245,236,99,0.25)',
  menuLineShadow: '0 0 10px rgba(167, 231, 104, 0.18)',
  epPriceShadow: '0 0 10px rgba(252,215,73,0.4)',
  iconShadow: '0 0 5.948px 0 rgba(245, 236, 99, 0.40), 0 -2.348px 3.13px 0.783px rgba(167, 231, 104, 0.40) inset',
  otherShadow: '0 3px 12.1px 5px rgba(245, 236, 99, 0.30) inset',
  featureShadow: '0 -3px 7.2px 2px rgba(245, 236, 99, 0.30) inset',
  assetShadow: '0 3px 4px 0 rgba(245, 236, 99, 0.18) inset',
  shadowDrawer: '0 3px 14.6px 0 rgba(245, 236, 99, 0.30) inset',
  buttonShadow: '0 2px 21px 0 #a4b3fc26',
  dropdownShadow: '0 10px 28px rgba(0,0,0,0.55), 0 0 18px rgba(245,236,99,0.20)',
} as const;

/**
 * Interactive element colors
 */
export const INTERACTIVE_COLORS = {
  optionActiveBg: 'rgba(173, 215, 65, 0.20)',
  optionHoverBg: 'rgba(245, 236, 99, 0.10)',
  paymentSelectedBorder: 'rgba(91,164,234,0.55)',
  paymentUnselectedBorder: 'rgba(191,192,224,0.18)',
  paymentSelectedText: '#5BA4EA',
  paymentUnselectedText: 'rgba(208,208,229,0.75)',
  recentTimeText: '#6B7280',
} as const;

/**
 * Modal & overlay colors
 */
export const MODAL_COLORS = {
  overlay: 'rgba(0,0,0,0.70)',
  overlayBlur: 'blur(4px)',
  bg: 'rgba(4,16,30,0.86)',
  blur: 'blur(14px)',
} as const;

/**
 * Live & status indicators
 */
export const LIVE_COLORS = {
  live: '#4ACA61',
  languageText: '#9CA3AF',
} as const;
